import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, where, orderBy, onSnapshot, doc, updateDoc, getDoc, serverTimestamp, deleteDoc, getDocs, writeBatch, Timestamp } from 'firebase/firestore';
import { ref, deleteObject } from 'firebase/storage';
import { db, storage } from '../../../firebaseConfig';
import { useError } from '../../../contexts/ErrorContext';

import { Capacitor } from '@capacitor/core';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { ActionSheet, ActionSheetButtonStyle } from '@capacitor/action-sheet';

import { useBillProcessing } from './useBillProcessing';

import { useOrganization } from '../../../contexts/OrganizationContext';
import { useSecurityVerification } from '../../../hooks/useSecurityVerification';
import { useSimplifiedSecurity } from '../../../hooks/useSimplifiedSecurity';

import { normalizePartyNameForStorage } from '../../../utils/platformUtils';
import { updateFileBackupInfoAfterUpload, updateFileBackupInfoAfterRemoval } from '../../../utils/fileUploadIntegration';
import { searchWithRelevance } from '../../../utils/searchUtils';
import { useTranslation } from '../../../hooks/useTranslation';

// Helper function to categorize orders by dispatch date
export function categorizeDispatchedOrders(orders) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  return orders.reduce((acc, order) => {
    // Ensure we have a valid date object
    let dispatchDate = null;
    if (order.dispatchedAt) {
      // Handle both Firestore Timestamp and regular Date objects
      dispatchDate = typeof order.dispatchedAt.toDate === 'function' 
        ? order.dispatchedAt.toDate() 
        : new Date(order.dispatchedAt);
    }
    
    if (!dispatchDate) return acc;

    // Normalize the time to midnight for comparison
    dispatchDate.setHours(0, 0, 0, 0);
    
    const dispatchTime = dispatchDate.getTime();
    const todayTime = today.getTime();
    const yesterdayTime = yesterday.getTime();

    if (dispatchTime === todayTime) {
      acc.today.push(order);
    } else if (dispatchTime === yesterdayTime) {
      acc.yesterday.push(order);
    } else if (dispatchTime < todayTime) {
      acc.older.push(order);
    }
    return acc;
  }, { today: [], yesterday: [], older: [] });
}

export function useOrders(user, currentTab = null, searchTerm = '', startDate = null, endDate = null, personFilter = '') {
  const { organizationId, userRole } = useOrganization();
  const { showErrorToast } = useError();
  const { t } = useTranslation('orders');
  
  // Initialize security verification (keeping for backward compatibility)
  const {
    verifyAccess,
    isMembershipVerified,
    verificationLoading: securityLoading,
    verificationError: securityError,
    verifyOrganizationMembership
  } = useSecurityVerification(organizationId);
  
  // Initialize simplified security (new approach)
  const {
    canUploadFileType,
    validateAccess,
    isAuthenticated
  } = useSimplifiedSecurity();
  
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);


  const [creatorNames, setCreatorNames] = useState({});
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [editingOrder, setEditingOrder] = useState(null);
  const [editFormData, setEditFormData] = useState({
    partyName: '',
    orderDetails: '',
    additionalNotes: ''
  });

  // Order Value Approval Modal State
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [selectedOrderForApproval, setSelectedOrderForApproval] = useState(null);

  // Use the new bill processing hook
  const {

    pendingBillOrderId,
    billPagesToProcess,
    setShowBillPageModal,
    setPendingBillOrderId,
    setBillPagesToProcess,
    processBillWithDocumentAI,
    closeBillPageModal,

    billPageModalProps: originalBillPageModalProps
  } = useBillProcessing(organizationId, setOrders);

  // Add state to track replaceIndex for bill replacement
  const [pendingBillReplaceIndex, setPendingBillReplaceIndex] = useState(null);
  // Add state to track whether we're in replace mode for bills
  const [isBillReplaceMode, setIsBillReplaceMode] = useState(false);


  // Cleanup effect for file operations
  useEffect(() => {
    return () => {
      // Cleanup function when component unmounts
      // This ensures any remaining file references are cleaned up
      try {
        // Clean up any remaining object URLs
        if (typeof window !== 'undefined' && window.URL && window.URL.revokeObjectURL) {
          // Note: We can't track all object URLs here, but this is a safety measure
          // The main cleanup happens in the ImageUploader component
        }
      } catch (error) {
        // Error during cleanup - handled silently
      }
    };
  }, []);

  // Filter orders based on user role - memoized to avoid recreation on each render
  const filterOrdersByRole = React.useCallback((orders) => {
    if (!userRole || !orders) return [];

    let filteredOrders = [];
    switch (userRole) {
      case 'Salesperson':
        filteredOrders = orders.filter(order => 
          order.createdBy === user?.uid || order.assignedTo === user?.uid
        );
        break;
      case 'BusinessOwner':
      case 'Admin':
      case 'Accountant':
        filteredOrders = orders;
        break;
      case 'InventoryManager':
        filteredOrders = orders.filter(order => 
          order.status === 'APPROVED' || 
          order.status === 'READY_TO_DISPATCH' || 
          order.status === 'DISPATCHED'
        );
        break;
      default:
        filteredOrders = [];
    }
    
    return filteredOrders;
  }, [userRole, user]);

  // Filter orders based on search term - memoized to avoid recreation on each render
  const filterOrdersBySearch = React.useCallback((orders) => {
    if (!searchTerm || !orders) return orders;
    
    // Use relevance-based search for better ranking
    return searchWithRelevance(orders, searchTerm, ['partyName', 'orderDetails']);
  }, [searchTerm]);

  // Filter orders based on person filter - memoized to avoid recreation on each render
  const filterOrdersByPerson = React.useCallback((orders) => {
    if (!personFilter || !orders) return orders;
    
    return orders.filter(order => {
      const creatorName = creatorNames[order.createdBy];
      const assignedName = creatorNames[order.assignedTo];
      
      return creatorName === personFilter || assignedName === personFilter;
    });
  }, [personFilter, creatorNames]);

  // FIXED: Extract heavy async operations outside listener to prevent memory buildup
  const fetchCreatorNames = useCallback(async (ordersData) => {
    if (!organizationId) return;
    
    const creatorIds = new Set(
      ordersData.flatMap((order) => [order.createdBy, order.assignedTo].filter(Boolean))
    );
    const newCreatorNames = { ...creatorNames };
    let namesUpdated = false;

    for (const creatorId of creatorIds) {
      if (!creatorId || newCreatorNames[creatorId]) continue;
      try {
        const userDoc = await getDoc(doc(db, `organizations/${organizationId}/users/${creatorId}`));
        if (userDoc.exists()) {
          newCreatorNames[creatorId] = userDoc.data().fullName;
          namesUpdated = true;
        }
      } catch (err) {
        // Error fetching user - handled silently
      }
    }

    if (namesUpdated) {
      setCreatorNames(newCreatorNames);
    }
  }, [organizationId, creatorNames]);

  // Fetch orders when dependencies change
  useEffect(() => {
    if (!organizationId || !user) {
      setOrders([]);
      setFilteredOrders([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    
    // Track active timers for cleanup to prevent memory leaks
    const activeTimers = new Set();

    function subscribeToOrders() {
      try {
        // Get orders from the organization's orders collection
        const ordersRef = collection(db, `organizations/${organizationId}/orders`);
        const ordersQuery = query(ordersRef, orderBy('createdAt', 'desc'));

        const unsubscribe = onSnapshot(ordersQuery, 
          async (snapshot) => {
            try {
              const ordersData = snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                  id: doc.id,
                  ...data,
                  // Safely handle createdAt timestamp
                  createdAt: data.createdAt ? 
                    (typeof data.createdAt.toDate === 'function' ? 
                      data.createdAt.toDate() : new Date(data.createdAt)) 
                    : new Date(),
                  // Safely handle dispatchDate timestamp
                  dispatchDate: data.dispatchDate ? 
                    (typeof data.dispatchDate.toDate === 'function' ? 
                      data.dispatchDate.toDate() : new Date(data.dispatchDate)) 
                    : null,
                  // Safely handle dispatchedAt timestamp
                  dispatchedAt: data.dispatchedAt ? 
                    (typeof data.dispatchedAt.toDate === 'function' ? 
                      data.dispatchedAt.toDate() : new Date(data.dispatchedAt)) 
                    : null
                };
              });

              // Apply role-based filtering
              let filtered = filterOrdersByRole(ordersData);

              // Apply search filtering
              filtered = filterOrdersBySearch(filtered);

              // Apply person filtering
              filtered = filterOrdersByPerson(filtered);

              // Set states
              setOrders(ordersData);
              setFilteredOrders(filtered);
              setLoading(false);
              setError(null);

              // FIXED: Track setTimeout for proper cleanup to prevent memory leaks
              // Schedule creator name fetching for next tick to avoid blocking the listener
              const timerId = setTimeout(() => {
                fetchCreatorNames(ordersData);
                activeTimers.delete(timerId); // Remove from tracking when completed
              }, 0);
              activeTimers.add(timerId); // Track the timer
            } catch (err) {
              setError(`Error processing orders: ${err.message}`);
              setLoading(false);
            }
          },
          (err) => {
            setError(`Error fetching orders: ${err.message}`);
            setLoading(false);

            // FIXED: Disable retry logic to prevent memory leaks
            // Retry logic creates new subscriptions without cleanup
            // For now, we'll rely on the useEffect dependency array to recreate
            // the subscription when needed, which is safer
          }
        );

        return () => {
          unsubscribe();
          // FIXED: Clean up all active timers to prevent memory leaks
          activeTimers.forEach(timerId => {
            clearTimeout(timerId);
          });
          activeTimers.clear();
        };
      } catch (err) {
        setError(`Error setting up orders subscription: ${err.message}`);
        setLoading(false);
        return () => {
          // FIXED: Clean up timers even in error case
          activeTimers.forEach(timerId => {
            clearTimeout(timerId);
          });
          activeTimers.clear();
        };
      }
    }

    const unsubscribe = subscribeToOrders();
    return () => unsubscribe();
  }, [organizationId, user, filterOrdersByRole, filterOrdersBySearch, filterOrdersByPerson, fetchCreatorNames]);

  // Filter orders by current tab and date range
  const displayedOrders = React.useMemo(() => {
    if (!currentTab) return filteredOrders;

    const filtered = filteredOrders.filter(order => {
      const matchesTab = order.status === currentTab;
      
      if (currentTab === 'DISPATCHED' && matchesTab && (startDate || endDate)) {
        let dispatchDate = null;
        if (order.dispatchedAt) {
          // Handle both Firestore Timestamp and regular Date objects
          dispatchDate = typeof order.dispatchedAt.toDate === 'function'
            ? order.dispatchedAt.toDate()
            : new Date(order.dispatchedAt);
        }
        
        if (!dispatchDate) return matchesTab; // Still show dispatched orders even without date

        const start = startDate ? new Date(startDate) : null;
        const end = endDate ? new Date(endDate) : null;

        if (start && end) {
          end.setHours(23, 59, 59, 999);
          return dispatchDate >= start && dispatchDate <= end;
        } else if (start) {
          return dispatchDate >= start;
        } else if (end) {
          end.setHours(23, 59, 59, 999);
          return dispatchDate <= end;
        }
      }
      
      return matchesTab;
    });

    // If we're on the dispatched tab, categorize the orders
    if (currentTab === 'DISPATCHED') {
      const categorized = categorizeDispatchedOrders(filtered);
      return categorized;
    }

    return filtered;
  }, [filteredOrders, currentTab, startDate, endDate]);

  // Fix existing order URLs
  useEffect(() => {
    // Check if migration has already been completed for this session
    const migrationFlagKey = `orderUrlMigration_${organizationId}`;
    const migrationCompleted = sessionStorage.getItem(migrationFlagKey);
    
    if (orders.length > 0 && organizationId && !migrationCompleted) {
      // Check for file URLs that need fixing
      const ordersWithOldUrls = orders.filter(order => 
        (order.challanURL && !order.challanURL.includes(`${organizationId}/challan`)) ||
        (order.billURL && !order.billURL.includes(`${organizationId}/bill`)) ||
        (order.biltyURL && !order.biltyURL.includes(`${organizationId}/bilty`)) ||
        (order.billURL && !order.billURLs) || // Add check for old billURL format
        (order.biltyURL && !order.biltyURLs) // Add check for old biltyURL format
      );
      
      if (ordersWithOldUrls.length > 0) {
        // Migrate old billURL to billURLs and biltyURL to biltyURLs
        const migratePromises = ordersWithOldUrls.map(async (order) => {
          const updates = {};
          
          if (order.billURL && !order.billURLs) {
            updates.billURLs = [order.billURL];
            updates.billURL = null; // Remove the old field
          }
          
          if (order.biltyURL && !order.biltyURLs) {
            updates.biltyURLs = [order.biltyURL];
            updates.biltyURL = null; // Remove the old field
          }

          if (Object.keys(updates).length > 0) {
            try {
              const orderRef = doc(db, `organizations/${organizationId}/orders/${order.id}`);
              await updateDoc(orderRef, updates);
            } catch (error) {
              // Failed to migrate URLs - handled silently
            }
          }
        });

        // Execute migrations
        Promise.all(migratePromises).then(() => {
          // Set migration flag after successful completion
          sessionStorage.setItem(migrationFlagKey, 'true');
        }).catch(error => {
          // Failed to migrate some URLs - handled silently
        });
      } else {
        // No migrations needed, set flag anyway
        sessionStorage.setItem(migrationFlagKey, 'true');
      }
    }
  }, [organizationId, orders]);
  
  const viewFile = (originalUrl) => {
    // Simply return the original URL without modification
    return originalUrl;
  };

  const viewChallan = (url) => viewFile(url, 'challan');
  const viewBill = (url) => viewFile(url, 'bill');
  const viewBilty = (url) => viewFile(url, 'bilty');

  // Order management functions
  const handleApprove = async (orderId) => {
    // ✅ SIMPLIFIED SECURITY: Single check for user experience
    if (!isAuthenticated) {
      showErrorToast(t('errors.pleaseLoginAndSelectOrg'), {
        variant: 'error',
        autoHide: true,
        duration: 4000
      });
      return;
    }

    // Check if user can approve orders (UX check - Firebase rules handle real security)
    const validation = validateAccess('CAN_APPROVE_ORDERS');
    if (!validation.hasAccess) {
      showErrorToast(validation.errorMessage, {
        variant: 'error',
        autoHide: true,
        duration: 4000
      });
      return;
    }

    try {
      const orderRef = doc(db, `organizations/${organizationId}/orders/${orderId}`);
      await updateDoc(orderRef, {
        status: 'APPROVED',
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      throw error;
    }
  };

  const handleEdit = (order) => {
    setEditingOrder(order);
    setEditFormData({
      partyName: order.partyName,
      orderDetails: order.orderDetails,
      additionalNotes: order.additionalNotes || ''
    });
  };

  const handleEditSubmit = async (orderId, formData) => {
    // ✅ SIMPLIFIED SECURITY: Single check for user experience
    if (!isAuthenticated) {
      showErrorToast(t('errors.pleaseLoginAndSelectOrg'), {
        variant: 'error',
        autoHide: true,
        duration: 4000
      });
      return;
    }

    // Check if user can edit orders (UX check - Firebase rules handle real security)
    const validation = validateAccess('CAN_EDIT_ORDERS');
    if (!validation.hasAccess) {
      showErrorToast(validation.errorMessage, {
        variant: 'error',
        autoHide: true,
        duration: 4000
      });
      return;
    }

    try {
      await updateDoc(doc(db, `organizations/${organizationId}/orders/${orderId}`), {
        partyName: normalizePartyNameForStorage(formData.partyName),
        orderDetails: formData.orderDetails,
        additionalNotes: formData.additionalNotes,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      throw error;
    }
  };

  const handleEditChange = (e) => {
    setEditFormData({ ...editFormData, [e.target.name]: e.target.value });
  };

  const handleDispatch = async (orderId) => {
    // ✅ SECURITY VERIFICATION: Verify user has access before proceeding
    if (!isMembershipVerified) {
      showErrorToast(t('errors.accessDeniedSecurityFailed'), {
        variant: 'error',
        autoHide: true,
        duration: 4000
      });
      return;
    }

    // Verify user has the required role for dispatch
    if (userRole !== 'BusinessOwner') {
      const hasRequiredRole = await verifyAccess('InventoryManager');
      if (!hasRequiredRole) {
        showErrorToast(t('errors.accessDeniedRequiresRole', { role: 'InventoryManager' }), {
          variant: 'error',
          autoHide: true,
          duration: 4000
        });
        return;
      }
    }

    try {
      const orderRef = doc(db, `organizations/${organizationId}/orders/${orderId}`);
      await updateDoc(orderRef, {
        status: 'DISPATCHED',
        dispatchedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      throw error;
    }
  };

  const handleReadyToDispatch = async (orderId) => {
    // ✅ SECURITY VERIFICATION: Verify user has access before proceeding
    if (!isMembershipVerified) {
      showErrorToast(t('errors.accessDeniedSecurityFailed'), {
        variant: 'error',
        autoHide: true,
        duration: 4000
      });
      return;
    }

    // Verify user has the required role for ready to dispatch
    if (userRole !== 'BusinessOwner') {
      const hasRequiredRole = await verifyAccess(['InventoryManager', 'Accountant']);
      if (!hasRequiredRole) {
        showErrorToast(t('errors.accessDeniedRequiresRoles', { roles: 'InventoryManager or Accountant' }), {
          variant: 'error',
          autoHide: true,
          duration: 4000
        });
        return;
      }
    }

    try {
      const orderRef = doc(db, `organizations/${organizationId}/orders/${orderId}`);
      await updateDoc(orderRef, {
        status: 'READY_TO_DISPATCH',
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      throw error;
    }
  };

  const renumberOrders = async (deletedOrderNumber) => {
    try {
      // Get all orders with orderNumber greater than the deleted order
      const ordersRef = collection(db, `organizations/${organizationId}/orders`);
      const q = query(ordersRef, where('orderNumber', '>', deletedOrderNumber));
      const querySnapshot = await getDocs(q);
      
      // Update each order's number
      const batch = writeBatch(db);
      querySnapshot.forEach((doc) => {
        const newOrderNumber = doc.data().orderNumber - 1;
        batch.update(doc.ref, { orderNumber: newOrderNumber });
      });
      
      // Update the lastOrderNumber in organization document
      const orgRef = doc(db, `organizations/${organizationId}`);
      const orgDoc = await getDoc(orgRef);
      const currentLastOrderNumber = orgDoc.data()?.lastOrderNumber || 0;
      batch.update(orgRef, { lastOrderNumber: currentLastOrderNumber - 1 });
      
      await batch.commit();
    } catch (error) {
      throw error;
    }
  };

  // Helper function to delete all files associated with an order
  const deleteOrderFiles = async (orderData) => {
    const fileDeletionErrors = [];
    
    // Define all file types to delete (arrays and legacy single URLs)
    const fileTypes = [
      { urls: orderData.orderDetailsImageURLs, name: 'orderDetails' },
      { urls: orderData.billURLs, name: 'bills' },
      { urls: orderData.challanURLs, name: 'challans' },
      { urls: orderData.biltyURLs, name: 'bilty' },
      { urls: orderData.productURLs, name: 'products' }
    ];
    
    // Also check for legacy single URL fields (only if array doesn't exist)
    // This prevents duplicate deletion if both legacy and array formats exist
    if (orderData.billURL && (!orderData.billURLs || orderData.billURLs.length === 0)) {
      fileTypes.push({ urls: [orderData.billURL], name: 'bills' });
    }
    if (orderData.challanURL && (!orderData.challanURLs || orderData.challanURLs.length === 0)) {
      fileTypes.push({ urls: [orderData.challanURL], name: 'challans' });
    }
    if (orderData.biltyURL && (!orderData.biltyURLs || orderData.biltyURLs.length === 0)) {
      fileTypes.push({ urls: [orderData.biltyURL], name: 'bilty' });
    }
    if (orderData.productURL && (!orderData.productURLs || orderData.productURLs.length === 0)) {
      fileTypes.push({ urls: [orderData.productURL], name: 'products' });
    }
    if (orderData.orderDetailsImageURL && (!orderData.orderDetailsImageURLs || orderData.orderDetailsImageURLs.length === 0)) {
      fileTypes.push({ urls: [orderData.orderDetailsImageURL], name: 'orderDetails' });
    }
    
    // Delete all files
    for (const fileType of fileTypes) {
      if (fileType.urls && Array.isArray(fileType.urls)) {
        for (const url of fileType.urls) {
          if (!url) continue;
          
          try {
            // Extract file path from URL
            const decodedUrl = decodeURIComponent(url);
            const filePathMatch = decodedUrl.match(/o\/(.+?)\?/);
            
            if (filePathMatch && filePathMatch[1]) {
              const fileRef = ref(storage, filePathMatch[1]);
              await deleteObject(fileRef);
            }
          } catch (error) {
            // Log error but don't fail the entire operation
            fileDeletionErrors.push({ fileType: fileType.name, url, error: error.message });
          }
        }
      }
    }
    
    // Return errors if any (for logging/monitoring)
    return fileDeletionErrors;
  };

  const handleCancel = async (orderId) => {
    if (!window.confirm(t('errors.confirmCancelOrder'))) {
      return;
    }

    // ✅ SECURITY VERIFICATION: Verify user has access before proceeding
    if (!isMembershipVerified) {
      showErrorToast(t('errors.accessDeniedSecurityFailed'), {
        variant: 'error',
        autoHide: true,
        duration: 4000
      });
      return;
    }

    // Verify user has the required role for cancellation
    if (userRole !== 'BusinessOwner') {
      const hasRequiredRole = await verifyAccess('BusinessOwner');
      if (!hasRequiredRole) {
        showErrorToast(t('errors.accessDeniedRequiresRole', { role: 'BusinessOwner' }), {
          variant: 'error',
          autoHide: true,
          duration: 4000
        });
        return;
      }
    }

    try {
      // Get the order to be deleted to get its orderNumber and file URLs
      const orderRef = doc(db, `organizations/${organizationId}/orders/${orderId}`);
      const orderDoc = await getDoc(orderRef);
      
      if (!orderDoc.exists()) {
        showErrorToast(t('errors.orderNotFound'), {
          variant: 'error',
          autoHide: true,
          duration: 4000
        });
        return;
      }
      
      const orderData = orderDoc.data();
      const deletedOrderNumber = orderData?.orderNumber;
      
      // Delete all associated files BEFORE deleting the order document
      // This ensures files are cleaned up immediately, not relying on Cloud Functions
      const fileDeletionErrors = await deleteOrderFiles(orderData);
      
      // Log file deletion errors (if any) but don't fail the order deletion
      if (fileDeletionErrors.length > 0) {
        // Some files failed to delete - handled silently
      }
      
      // Delete the order document
      await deleteDoc(orderRef);
      
      // Renumber remaining orders
      if (deletedOrderNumber) {
        await renumberOrders(deletedOrderNumber);
      }
      
      // Show success message
      // Use the correct namespace/key from orders.json -> messages.orderDeleted
      showErrorToast(t('messages.orderDeleted') || 'Order deleted successfully', {
        variant: 'info',
        autoHide: true,
        duration: 3000
      });
    } catch (error) {
      showErrorToast(t('errors.failedToDeleteOrder'), {
        variant: 'error',
        autoHide: true,
        duration: 4000
      });
      throw error;
    }
  };

  // File upload helpers
  const openFilePicker = async (accept, onFilesSelected) => {
    
    // For Android, we'll show a native menu of options.
    // For iOS and Web, the default file input is already excellent.
    if (Capacitor.getPlatform() === 'android') {
      const presentActionSheet = async () => {
        try {
          const result = await ActionSheet.showActions({
            title: 'Select Source',
            options: [
              { title: 'Camera', style: ActionSheetButtonStyle.Default },
              { title: 'Photo Gallery', style: ActionSheetButtonStyle.Default },
              { title: 'Files (Image or PDF)', style: ActionSheetButtonStyle.Default },
              { title: 'Cancel', style: ActionSheetButtonStyle.Cancel },
            ],
          });

          let file = null;

          try {
            switch (result.index) {
              case 0: // Camera
                const cameraImage = await Camera.getPhoto({
                  quality: 90,
                  allowEditing: false,
                  resultType: CameraResultType.DataUrl,
                  source: CameraSource.Camera,
                });
                if (cameraImage.dataUrl) {
                  const response = await fetch(cameraImage.dataUrl);
                  const blob = await response.blob();
                  file = new File([blob], `photo_${Date.now()}.jpg`, { type: 'image/jpeg' });
                  
                  // Clean up the blob after creating the file
                  try {
                    URL.revokeObjectURL(cameraImage.dataUrl);
                  } catch (error) {
                    // Error cleaning up camera image URL - handled silently
                  }
                }
                break;

              case 1: // Gallery
                const galleryImage = await Camera.getPhoto({
                  quality: 90,
                  allowEditing: false,
                  resultType: CameraResultType.DataUrl,
                  source: CameraSource.Photos,
                });
                if (galleryImage.dataUrl) {
                  const response = await fetch(galleryImage.dataUrl);
                  const blob = await response.blob();
                  file = new File([blob], `gallery_${Date.now()}.jpg`, { type: 'image/jpeg' });
                  
                  // Clean up the blob after creating the file
                  try {
                    URL.revokeObjectURL(galleryImage.dataUrl);
                  } catch (error) {
                    // Error cleaning up gallery image URL - handled silently
                  }
                }
                break;

              case 2: // Files
                // This is a workaround to call the original file picker logic
                // for selecting any type of file.
                selectFilesFromSystem(accept, onFilesSelected);
                return; // Exit because selectFilesFromSystem handles the callback

              default: // Cancel or dismiss
                return;
            }

            if (file) {
              // We wrap the single file in an object that mimics the structure
              // of a file input event, so the existing handler can use it.
              onFilesSelected({ target: { files: [file] } });
            }
          } catch (error) {
            // Ignore error if user cancelled the action
            if (error.message !== 'User cancelled photos app' && error.message !== 'No image selected') {
              showErrorToast(t('errors.couldNotSelectFile'), {
                variant: 'error'
              });
            }
          }
        } catch (error) {
          // Error presenting action sheet - handled silently
        }
      };

      presentActionSheet();

    } else {
      // For iOS and Web, use the original, simpler method.
      selectFilesFromSystem(accept, onFilesSelected);
    }
  };

  // The original file picker logic, now extracted to its own function
  // so it can be called by both the Android and iOS/Web paths.
  const selectFilesFromSystem = (accept, onFilesSelected) => {
    
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = accept;          // e.g. 'image/*,application/pdf'
    input.multiple = true;
    input.style.display = 'none';
    document.body.appendChild(input);
  
    // 2. Cleanup function: remove listener and DOM node
    const cleanup = () => {
      try {
        input.removeEventListener('change', onChange);
        input.removeEventListener('cancel', onCancel);
        if (input.parentNode) {
          input.parentNode.removeChild(input);
        }
      } catch (error) {
        // Silently handle cleanup errors to prevent breaking functionality
      }
    };
  
    // 3. Handle file selection
    const onChange = async (e) => {
      
      if (e.target.files.length) {
        try {
          await onFilesSelected(e);
        } catch (error) {
          // Error in file selection handler - handled silently
        }
      } else {
        // No files selected
      }
      cleanup();
    };

    // 4. Handle cancellation (for browsers that support it)
    const onCancel = () => {
      cleanup();
    };
  
    input.addEventListener('change', onChange);
    input.addEventListener('cancel', onCancel);
    
    // 5. Launch the picker
    input.click();

    // 6. Add a safety timeout to ensure cleanup happens even if events don't fire
    // This prevents memory leaks in edge cases while not interfering with normal operation
    setTimeout(() => {
      if (input.parentNode) {
        cleanup();
      }
    }, 30000); // 30 second safety timeout
  };

  // Add a function to set up product image deletion
  const setupProductImageDeletion = async (orderId, productURLs) => {
    try {
      // FIX: Use Firestore Timestamp instead of JavaScript Date for consistency
      const now = Timestamp.now();
      const tenDaysLater = new Date(now.toDate());
      tenDaysLater.setDate(tenDaysLater.getDate() + 10); // 10 days from now
      const productDeletionDate = Timestamp.fromDate(tenDaysLater);
      
      const orderRef = doc(db, `organizations/${organizationId}/orders/${orderId}`);
      await updateDoc(orderRef, {
        productURLs,
        productDeletionDate: productDeletionDate,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      throw error;
    }
  };

  // Update handleUploadFile to use the new setup function
  const handleUploadFile = async (orderId, fileType, updateStatus = false, isReplacing = false, replaceIndex = null, pagesToProcess = undefined) => {
    
    // ✅ INPUT VALIDATION: Validate parameters before proceeding
    if (!orderId || !fileType) {
      showErrorToast(t('errors.missingRequiredParams'), {
        variant: 'error',
        autoHide: true,
        duration: 4000
      });
      return;
    }

    // Validate file type
    const validFileTypes = ['bill', 'challan', 'bilty', 'product', 'order', 'invoice'];
    if (!validFileTypes.includes(fileType)) {
      showErrorToast(t('errors.invalidFileType', { fileType, types: validFileTypes.join(', ') }), {
        variant: 'error',
        autoHide: true,
        duration: 4000
      });
      return;
    }

    // ✅ SIMPLIFIED SECURITY: Single check for user experience
    if (!isAuthenticated) {
      showErrorToast(t('errors.pleaseLoginAndSelectOrg'), {
        variant: 'error',
        autoHide: true,
        duration: 4000
      });
      return;
    }

    // Check if user can upload this file type (UX check - Firebase rules handle real security)
    if (!canUploadFileType(fileType)) {
      // Get the appropriate permission based on file type
      let permission = 'CAN_UPLOAD_ORDERS'; // default
      switch (fileType) {
        case 'bill': permission = 'CAN_UPLOAD_BILLS'; break;
        case 'challan': case 'bilty': permission = 'CAN_UPLOAD_CHALLANS'; break;
        case 'product': permission = 'CAN_UPLOAD_PRODUCTS'; break;
        case 'order': case 'invoice': permission = 'CAN_UPLOAD_ORDERS'; break;
        default: break; // Keep default permission value
      }
      const validation = validateAccess(permission);
      showErrorToast(validation.errorMessage || 'You don\'t have permission to upload this file type', {
        variant: 'error',
        autoHide: true,
        duration: 4000
      });
      return;
    }
    
    const handleFileChange = async (e) => {
      
      const files = Array.from(e.target.files);
      
      if (!files.length) {
        return;
      }

      try {
        
        // TEMPORARY DISABLE: Document AI processing is temporarily disabled
        // For bills, start Document AI processing immediately (before upload)
        let processPromise = null;
        if (fileType === 'bill') {
          const file = files[0];
          // Use pagesToProcess if provided, otherwise process all pages (undefined)
          processPromise = processBillWithDocumentAI(file, orderId, pagesToProcess, isReplacing ? replaceIndex : null, isReplacing);
        }
        
        // If replacing, delete old files first and track count for backup info
        let deletedFileCount = 0;
        if (isReplacing) {
          const orderRef = doc(db, `organizations/${organizationId}/orders/${orderId}`);
          const orderDoc = await getDoc(orderRef);
          
          // Handle multiple URLs for challans, bills, biltys, and products
          if (fileType === 'challan' || fileType === 'bill' || fileType === 'bilty' || fileType === 'product') {
            const oldFileURLs = orderDoc.data()?.[`${fileType}URLs`] || [];
            
            if (replaceIndex !== null) {
              // Delete only the specific file being replaced
              const oldFileURL = oldFileURLs[replaceIndex];
              if (oldFileURL) {
                try {
                  const decodedUrl = decodeURIComponent(oldFileURL);
                  const filePathMatch = decodedUrl.match(/o\/(.+?)\?/);
                  if (filePathMatch && filePathMatch[1]) {
                    const oldFileRef = ref(storage, filePathMatch[1]);
                    await deleteObject(oldFileRef);
                    deletedFileCount = 1; // Track that we deleted 1 file
                  }
                } catch (error) {
                  // Error deleting old file - handled silently
                }
              }
            } else {
              // Delete all existing files
              for (const oldFileURL of oldFileURLs) {
                try {
                  const decodedUrl = decodeURIComponent(oldFileURL);
                  const filePathMatch = decodedUrl.match(/o\/(.+?)\?/);
                  if (filePathMatch && filePathMatch[1]) {
                    const oldFileRef = ref(storage, filePathMatch[1]);
                    await deleteObject(oldFileRef);
                    deletedFileCount++; // Track each deleted file
                  }
                } catch (error) {
                  // Error deleting old file - handled silently
                }
              }
            }
          } else {
            // Handle single file URLs for other types
            const oldFileURL = orderDoc.data()?.[`${fileType}URL`];
            if (oldFileURL) {
              try {
                const decodedUrl = decodeURIComponent(oldFileURL);
                const filePathMatch = decodedUrl.match(/o\/(.+?)\?/);
                if (filePathMatch && filePathMatch[1]) {
                  const oldFileRef = ref(storage, filePathMatch[1]);
                  await deleteObject(oldFileRef);
                  deletedFileCount = 1; // Track that we deleted 1 file
                }
              } catch (error) {
                // Error deleting old file - handled silently
              }
            }
          }
        }
        
        // Upload files with retry logic and offline detection
        const { uploadFilesWithRetry } = await import('../../../utils/fileUploadUtils');
        
        const uploadResults = await uploadFilesWithRetry(
          files,
          `${organizationId}/${fileType}`,
          {
            maxRetries: 3,
            onFileProgress: (fileIndex, success, downloadURL, error) => {
              // File upload progress - handled silently
            }
          }
        );
        
        // Extract successful uploads
        const downloadURLs = uploadResults.successful.map(result => result.downloadURL);
        
        // Handle failed uploads
        if (uploadResults.failed.length > 0) {
          const failedCount = uploadResults.failed.length;
          const successCount = uploadResults.successful.length;
          
          if (successCount === 0) {
            // All files failed
            throw new Error(`Failed to upload ${failedCount} file(s). Please check your connection and try again.`);
          } else {
            // Some files failed, warn user but continue
            showErrorToast(
              `Warning: ${failedCount} file(s) failed to upload. ${successCount} file(s) uploaded successfully.`,
              {
                variant: 'warning',
                autoHide: true,
                duration: 6000
              }
            );
          }
        }

        // Clean up file references after successful upload
        files.forEach(file => {
          try {
            // Release any object URLs that might have been created for this file
            if (file.objectURL) {
              URL.revokeObjectURL(file.objectURL);
            }
          } catch (error) {
            // Error cleaning up file reference - handled silently
          }
        });

        const updates = {
          updatedAt: serverTimestamp()
        };

        // --- ENSURE ONLY VALID DOWNLOAD URLS ARE STORED FOR BILLS ---
        // Filter out any non-https URLs (should never happen, but extra safety)
        let safeDownloadURLs = downloadURLs;
        if (fileType === 'bill') {
          safeDownloadURLs = downloadURLs.filter(url => typeof url === 'string' && url.startsWith('https://'));
          if (safeDownloadURLs.length !== downloadURLs.length) {
            // One or more bill URLs are not valid download URLs - handled silently
          }
        }
        // Use safeDownloadURLs for bills, original for others
        // FIX: Calculate actual number of files that will be stored in database
        const actualFilesStored = fileType === 'bill' ? safeDownloadURLs.length : downloadURLs.length;
        
        if (fileType === 'challan' || fileType === 'bill' || fileType === 'bilty' || fileType === 'product') {
          const orderRef = doc(db, `organizations/${organizationId}/orders/${orderId}`);
          const orderDoc = await getDoc(orderRef);
          const existingURLs = orderDoc.data()?.[`${fileType}URLs`] || [];
          if (isReplacing && replaceIndex !== null) {
            // Replace single file at specific index
            const newURLs = [...existingURLs];
            newURLs[replaceIndex] = fileType === 'bill' ? safeDownloadURLs[0] : downloadURLs[0];
            updates[`${fileType}URLs`] = newURLs;
          } else if (isReplacing) {
            // Replace all files
            updates[`${fileType}URLs`] = fileType === 'bill' ? safeDownloadURLs : downloadURLs;
          } else {
            // Add new files to existing list
            updates[`${fileType}URLs`] = [...existingURLs, ...(fileType === 'bill' ? safeDownloadURLs : downloadURLs)];
          }
          // If it's a product image, set up deletion
          if (fileType === 'product') {
            await setupProductImageDeletion(orderId, updates[`${fileType}URLs`]);
          }
        } else {
          // For other file types, store single URL
          updates[`${fileType}URL`] = downloadURLs[0];
        }
        
        const orderRef = doc(db, `organizations/${organizationId}/orders/${orderId}`);
        await updateDoc(orderRef, updates);
        
        // Update file backup info for file retention tracking
        try {
          // If replacing, first remove old files from backup tracking
          if (isReplacing && deletedFileCount > 0) {
            await updateFileBackupInfoAfterRemoval(organizationId, orderId, fileType, deletedFileCount);
          }
          // Then add new files to backup tracking (use actual files stored, not files.length)
          // FIX: Use actualFilesStored instead of files.length to match what's actually in the database
          await updateFileBackupInfoAfterUpload(organizationId, orderId, fileType, actualFilesStored);
        } catch (error) {
          // Don't throw error - file upload was successful, just backup tracking failed
        }
        
        // Wait for both upload and processing to complete (if processing was started)
        if (processPromise) {
          
          try {
            const processResult = await processPromise;
            
            if (processResult.success) {
              const organizationRef = doc(db, 'organizations', organizationId);
              const orgDoc = await getDoc(organizationRef);
              const currentPlan = orgDoc.data()?.subscriptionPlan || 'basic';
              const backupDays = currentPlan === 'enterprise' ? 'forever' : 
                                currentPlan === 'premiumPlus' ? '90 days' :
                                currentPlan === 'premium' ? '65 days' : '30 days';
              
              if (processResult.totalAmount === 0) {
                showErrorToast(t('errors.billUploadedSuccessEditAmount', { days: backupDays }), {
                  variant: 'info',
                  duration: 6000
                });
              } else if (processResult.totalAmount) {
                showErrorToast(t('errors.billUploadedProcessedExtracted', { amount: processResult.totalAmount, days: backupDays }), {
                  variant: 'info',
                  duration: 6000
                });
              } else {
                showErrorToast(t('errors.billUploadedProcessedNoAmount', { days: backupDays }), {
                  variant: 'info',
                  duration: 6000
                });
              }
            } else {
              showErrorToast(t('errors.billUploadedWithError', { error: processResult.error }), {
                variant: 'warning',
                showRetry: false,
                duration: 6000
              });
            }
          } catch (error) {
            showErrorToast(t('errors.billUploadedErrorSettingValue'), {
              variant: 'warning',
              showRetry: false,
              duration: 6000
            });
          }
        } else {
          // For non-bill files, show regular success message with backup info
          const organizationRef = doc(db, 'organizations', organizationId);
          const orgDoc = await getDoc(organizationRef);
          const currentPlan = orgDoc.data()?.subscriptionPlan || 'basic';
          const backupDays = currentPlan === 'enterprise' ? 'forever' : 
                            currentPlan === 'premiumPlus' ? '90 days' :
                            currentPlan === 'premium' ? '65 days' : '30 days';
          
          showErrorToast(t('errors.fileTypeUploadedSuccess', { fileType: t(`fileTypes.${fileType}`) || fileType.charAt(0).toUpperCase() + fileType.slice(1), days: backupDays }), {
            variant: 'info',
            showRetry: false,
            duration: 5000
          });
        }
        
        // If it's a product image, refresh the order data
        if (fileType === 'product') {
          const updatedOrderDoc = await getDoc(orderRef);
          if (updatedOrderDoc.exists()) {
            const updatedOrder = updatedOrderDoc.data();
            setOrders(prevOrders => {
              if (Array.isArray(prevOrders)) {
                return prevOrders.map(order => 
                  order.id === orderId ? { ...order, ...updatedOrder } : order
                );
              }
              return prevOrders;
            });
          }
        }
        
      } catch (err) {
        throw err;
      }
    };

    openFilePicker('image/*,application/pdf', handleFileChange);
  };

  const replaceFile = async (orderId, fileType, replaceIndex = null) => {
    if (!window.confirm(`Are you sure you want to replace the ${fileType}${replaceIndex !== null ? ' at position ' + (replaceIndex + 1) : '(s)'}?`)) {
      return;
    }

    // ✅ INPUT VALIDATION: Validate parameters before proceeding
    if (!orderId || !fileType) {
      showErrorToast(t('errors.missingRequiredParams'), {
        variant: 'error',
        autoHide: true,
        duration: 4000
      });
      return;
    }

    // Validate file type
    const validFileTypes = ['bill', 'challan', 'bilty', 'product', 'order', 'invoice'];
    if (!validFileTypes.includes(fileType)) {
      showErrorToast(t('errors.invalidFileType', { fileType, types: validFileTypes.join(', ') }), {
        variant: 'error',
        autoHide: true,
        duration: 4000
      });
      return;
    }

    // ✅ PRE-VERIFICATION: Ensure membership is verified before proceeding
    if (!isMembershipVerified && !securityLoading) {
      try {
        // Force a membership verification check
        const isMember = await verifyOrganizationMembership();
        if (!isMember) {
          showErrorToast(t('errors.accessDeniedSecurityFailed'), {
            variant: 'error',
            autoHide: true,
            duration: 4000
          });
          return;
        }
      } catch (error) {
        showErrorToast('Access denied: Security verification failed', {
          variant: 'error',
          autoHide: true,
          duration: 4000
        });
        return;
      }
    }

    // ✅ SECURITY VERIFICATION: Verify user has access before proceeding
    if (!isMembershipVerified) {
      showErrorToast(t('errors.accessDeniedSecurityFailed'), {
        variant: 'error',
        autoHide: true,
        duration: 4000
      });
      return;
    }

    // Determine required role based on file type
    let requiredRole = null;
    switch (fileType) {
      case 'bill':
        requiredRole = ['Accountant'];
        break;
      case 'challan':
      case 'bilty':
        requiredRole = ['Accountant', 'InventoryManager'];
        break;
      case 'product':
        requiredRole = ['InventoryManager'];
        break;
      default:
        requiredRole = ['Salesperson'];
    }

    // ✅ BUSINESS OWNER CHECK: BusinessOwner users have access to everything
    if (userRole !== 'BusinessOwner') {
      // Verify user has the required role (only for non-BusinessOwner users)
      try {
        const hasRequiredRole = await verifyAccess(requiredRole);
        if (!hasRequiredRole) {
          showErrorToast(t('errors.accessDeniedRequiresRoles', { roles: requiredRole.join(' or ') }), {
            variant: 'error',
            autoHide: true,
            duration: 4000
          });
          return;
        }
      } catch (error) {
        showErrorToast('Access denied: Security verification failed', {
          variant: 'error',
          autoHide: true,
          duration: 4000
        });
        return;
      }
    }

    try {
      // First, open file picker and get new files
      const handleFileChange = async (e) => {
        const files = Array.from(e.target.files);
        if (!files.length) return;

        try {
          // Now that we have new files, delete old ones and track count for backup info
          const orderRef = doc(db, `organizations/${organizationId}/orders/${orderId}`);
          const orderDoc = await getDoc(orderRef);
          let deletedFileCount = 0;
          
          // Handle multiple URLs for challans, bills, biltys, and products
          if (fileType === 'challan' || fileType === 'bill' || fileType === 'bilty' || fileType === 'product') {
            const oldFileURLs = orderDoc.data()?.[`${fileType}URLs`] || [];
            
            if (replaceIndex !== null) {
              // Delete only the specific file being replaced
              const oldFileURL = oldFileURLs[replaceIndex];
              if (oldFileURL) {
                try {
                  const decodedUrl = decodeURIComponent(oldFileURL);
                  const filePathMatch = decodedUrl.match(/o\/(.+?)\?/);
                  if (filePathMatch && filePathMatch[1]) {
                    const oldFileRef = ref(storage, filePathMatch[1]);
                    await deleteObject(oldFileRef);
                    deletedFileCount = 1; // Track that we deleted 1 file
                  }
                } catch (error) {
                  // Error deleting old file - handled silently
                }
              }
            } else {
              // Delete all existing files
              for (const oldFileURL of oldFileURLs) {
                try {
                  const decodedUrl = decodeURIComponent(oldFileURL);
                  const filePathMatch = decodedUrl.match(/o\/(.+?)\?/);
                  if (filePathMatch && filePathMatch[1]) {
                    const oldFileRef = ref(storage, filePathMatch[1]);
                    await deleteObject(oldFileRef);
                    deletedFileCount++; // Track each deleted file
                  }
                } catch (error) {
                  // Error deleting old file - handled silently
                }
              }
            }
          } else {
            // Handle single file URLs for other types
            const oldFileURL = orderDoc.data()?.[`${fileType}URL`];
            if (oldFileURL) {
              try {
                const decodedUrl = decodeURIComponent(oldFileURL);
                const filePathMatch = decodedUrl.match(/o\/(.+?)\?/);
                if (filePathMatch && filePathMatch[1]) {
                  const oldFileRef = ref(storage, filePathMatch[1]);
                  await deleteObject(oldFileRef);
                  deletedFileCount = 1; // Track that we deleted 1 file
                }
              } catch (error) {
                // Error deleting old file - handled silently
              }
            }
          }

          // Upload new files with retry logic and offline detection
          const { uploadFilesWithRetry } = await import('../../../utils/fileUploadUtils');
          
          const uploadResults = await uploadFilesWithRetry(
            files,
            `${organizationId}/${fileType}`,
            {
              maxRetries: 3,
              onFileProgress: (fileIndex, success, downloadURL, error) => {
                // File upload progress - handled silently
              }
            }
          );
          
          // Extract successful uploads
          const downloadURLs = uploadResults.successful.map(result => result.downloadURL);
          
          // Handle failed uploads
          if (uploadResults.failed.length > 0) {
            const failedCount = uploadResults.failed.length;
            const successCount = uploadResults.successful.length;
            
            if (successCount === 0) {
              // All files failed
              throw new Error(`Failed to upload ${failedCount} file(s). Please check your connection and try again.`);
            } else {
              // Some files failed, warn user but continue
              showErrorToast(
                `Warning: ${failedCount} file(s) failed to upload. ${successCount} file(s) uploaded successfully.`,
                {
                  variant: 'warning',
                  autoHide: true,
                  duration: 6000
                }
              );
            }
          }

          const updates = {
            updatedAt: serverTimestamp()
          };

          // FIX: Calculate actual number of files that will be stored in database
          const actualFilesStored = downloadURLs.length;

          // For challans, bills, biltys, and products, handle array storage
          if (fileType === 'challan' || fileType === 'bill' || fileType === 'bilty' || fileType === 'product') {
            const existingURLs = orderDoc.data()?.[`${fileType}URLs`] || [];

            if (replaceIndex !== null) {
              // Replace single file at specific index
              const newURLs = [...existingURLs];
              newURLs[replaceIndex] = downloadURLs[0];
              updates[`${fileType}URLs`] = newURLs;
            } else {
              // Replace all files
              updates[`${fileType}URLs`] = downloadURLs;
            }

            // If it's a product image, set up deletion
            if (fileType === 'product') {
              await setupProductImageDeletion(orderId, updates[`${fileType}URLs`]);
            }
          } else {
            // For other file types, store single URL
            updates[`${fileType}URL`] = downloadURLs[0];
          }

          await updateDoc(orderRef, updates);
          
          // Update file backup info: remove old files, add new files
          try {
            // Remove old files from backup tracking
            if (deletedFileCount > 0) {
              await updateFileBackupInfoAfterRemoval(organizationId, orderId, fileType, deletedFileCount);
            }
            // Add new files to backup tracking (use actual files stored, not files.length)
            // FIX: Use actualFilesStored instead of files.length to match what's actually in the database
            await updateFileBackupInfoAfterUpload(organizationId, orderId, fileType, actualFilesStored);
          } catch (error) {
            // Failed to update file backup info - handled silently
            // Don't throw error - file replacement was successful, just backup tracking failed
          }
          
          // If it's a product image, refresh the order data
          if (fileType === 'product') {
            const updatedOrderDoc = await getDoc(orderRef);
            if (updatedOrderDoc.exists()) {
              const updatedOrder = updatedOrderDoc.data();
              setOrders(prevOrders => {
                if (Array.isArray(prevOrders)) {
                  return prevOrders.map(order => 
                    order.id === orderId ? { ...order, ...updatedOrder } : order
                  );
                }
                return prevOrders;
              });
            }
          }
          
          showErrorToast(t('errors.fileTypeReplacedSuccess', { fileType: t(`fileTypes.${fileType}`) || fileType.charAt(0).toUpperCase() + fileType.slice(1) }), {
            variant: 'info',
            showRetry: false,
            duration: 4000
          });
        } catch (err) {
          // Error replacing file - handled silently
          showErrorToast(t('errors.errorReplacingFileType', { fileType: t(`fileTypes.${fileType}`) || fileType }), {
            variant: 'error',
          });
        }
      };

      // Open file picker
      openFilePicker('image/*,application/pdf', handleFileChange);
    } catch (error) {
      showErrorToast(t('errors.errorReplacingFileType', { fileType: t(`fileTypes.${fileType}`) || fileType }), {
        variant: 'error',
        showRetry: true
      });
    }
  };

  // Modified bill upload handler to directly upload without page selection modal
  const handleUploadBill = (orderId) => {
    setPendingBillReplaceIndex(null);
    setIsBillReplaceMode(false);
    // Skip the modal and directly call handleUploadFile with default pagesToProcess
    handleUploadFile(orderId, 'bill', true, false, null, undefined);
  };

  // Modified bill replace handler to directly upload without page selection modal
  const handleReplaceBill = (orderId, replaceIndex = null) => {
    setPendingBillReplaceIndex(replaceIndex);
    setIsBillReplaceMode(true);
    // Skip the modal and directly call handleUploadFile with default pagesToProcess
    handleUploadFile(orderId, 'bill', true, true, replaceIndex, undefined);
  };

  // Update confirmBillPageSelection to use replaceIndex
  const confirmBillPageSelection = useCallback((handleFileUpload) => {
    setShowBillPageModal(false);
    if (pendingBillOrderId) {
      const pagesToProcess = billPagesToProcess ? parseInt(billPagesToProcess, 10) : undefined;
      handleFileUpload(pendingBillOrderId, 'bill', true, isBillReplaceMode, pendingBillReplaceIndex, pagesToProcess);
      setPendingBillOrderId(null);
      setBillPagesToProcess('');
      setPendingBillReplaceIndex(null);
      setIsBillReplaceMode(false);
    }
  }, [pendingBillOrderId, billPagesToProcess, pendingBillReplaceIndex, isBillReplaceMode, setBillPagesToProcess, setPendingBillOrderId, setShowBillPageModal]);

  const handleUploadChallan = (orderId) => handleUploadFile(orderId, 'challan');
  const handleReplaceChallan = (orderId, replaceIndex = null) => replaceFile(orderId, 'challan', replaceIndex);
  const handleUploadBilty = (orderId) => handleUploadFile(orderId, 'bilty');
  const handleReplaceBilty = (orderId, replaceIndex = null) => replaceFile(orderId, 'bilty', replaceIndex);
  const handleUploadProduct = (orderId) => handleUploadFile(orderId, 'product');
  const handleReplaceProduct = (orderId, replaceIndex = null) => replaceFile(orderId, 'product', replaceIndex);

  const getAvailableTabs = () => {
    if (userRole === 'InventoryManager') {
      return ['APPROVED', 'READY_TO_DISPATCH', 'DISPATCHED'];
    }
    return ['PLACED', 'APPROVED', 'READY_TO_DISPATCH', 'DISPATCHED'];
  };

  // Delete an image from orderDetailsImageURLs
  const deleteOrderDetailsImage = async (orderId, imageUrl) => {
    try {
      // Remove from storage
      const decodedUrl = decodeURIComponent(imageUrl);
      const filePathMatch = decodedUrl.match(/o\/(.+?)\?/);
      if (filePathMatch && filePathMatch[1]) {
        const oldFileRef = ref(storage, filePathMatch[1]);
        await deleteObject(oldFileRef);
      }
      // Remove from Firestore array
      const orderRef = doc(db, `organizations/${organizationId}/orders/${orderId}`);
      const orderDoc = await getDoc(orderRef);
      const existingURLs = orderDoc.data()?.orderDetailsImageURLs || [];
      const newURLs = existingURLs.filter(url => url !== imageUrl);
      await updateDoc(orderRef, {
        orderDetailsImageURLs: newURLs,
        updatedAt: serverTimestamp()
      });
      
      // Update file backup info for file retention tracking
      try {
        await updateFileBackupInfoAfterRemoval(organizationId, orderId, 'orderDetails', 1);
      } catch (error) {
        // Failed to update file backup info after deletion - handled silently
        // Don't throw error - file deletion was successful, just backup tracking failed
      }
      
      showErrorToast(t('errors.imageDeletedSuccess'), {
        variant: 'info',
        showRetry: false,
        duration: 3000
      });
    } catch (error) {
      showErrorToast(t('errors.failedToDeleteImage'), {
        variant: 'error',
        showRetry: true
      });
      throw error;
    }
  };

  // Replace an image in orderDetailsImageURLs
  const replaceOrderDetailsImage = async (orderId, imageUrl) => {
    // Open file picker for a new image
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      if (!file.type.startsWith('image/')) {
        showErrorToast(t('errors.pleaseUploadImageFile'), {
          variant: 'warning',
          showRetry: false,
          duration: 4000
        });
        return;
      }
      try {
      // Remove old image from storage
      const decodedUrl = decodeURIComponent(imageUrl);
      const filePathMatch = decodedUrl.match(/o\/(.+?)\?/);
      if (filePathMatch && filePathMatch[1]) {
        const oldFileRef = ref(storage, filePathMatch[1]);
        await deleteObject(oldFileRef);
      }
        
        // Upload new image with retry logic and offline detection
        const { uploadFileWithRetry } = await import('../../../utils/fileUploadUtils');
        const fileRef = ref(storage, `${organizationId}/orderDetails/${orderId}_${Date.now()}_${file.name}`);
        const newUrl = await uploadFileWithRetry(fileRef, file, {
          maxRetries: 3
        });
        
      // Replace in Firestore array
      const orderRef = doc(db, `organizations/${organizationId}/orders/${orderId}`);
      const orderDoc = await getDoc(orderRef);
      const existingURLs = orderDoc.data()?.orderDetailsImageURLs || [];
      const newURLs = existingURLs.map(url => url === imageUrl ? newUrl : url);
      await updateDoc(orderRef, {
        orderDetailsImageURLs: newURLs,
        updatedAt: serverTimestamp()
      });
        
        // Update file backup info: remove old file, add new file
        try {
          // Remove old file from backup tracking
          await updateFileBackupInfoAfterRemoval(organizationId, orderId, 'orderDetails', 1);
          // Add new file to backup tracking
          await updateFileBackupInfoAfterUpload(organizationId, orderId, 'orderDetails', 1);
        } catch (error) {
          // Failed to update file backup info - handled silently
          // Don't throw error - file replacement was successful, just backup tracking failed
        }
        
      showErrorToast(t('errors.imageReplacedSuccess'), {
        variant: 'info',
        showRetry: false,
        duration: 3000
      });
      } catch (error) {
        showErrorToast(t('errors.failedToReplaceImage'), {
          variant: 'error',
          showRetry: true
        });
        throw error;
      }
    };
    input.click();
  };

  // Order Value Approval Functions
  const openApprovalModal = (order) => {
    setSelectedOrderForApproval(order);
    setShowApprovalModal(true);
  };

  const handleApproveOrderValue = async (orderId, newAmount) => {
    try {
      const orderRef = doc(db, `organizations/${organizationId}/orders/${orderId}`);
      await updateDoc(orderRef, {
        orderValue: newAmount,
        orderValueStatus: 'approved',
        updatedAt: serverTimestamp()
      });

      // Update local state
      setOrders(prevOrders => {
        if (Array.isArray(prevOrders)) {
          return prevOrders.map(order => 
            order.id === orderId 
              ? { ...order, orderValue: newAmount, orderValueStatus: 'approved' }
              : order
          );
        }
        return prevOrders;
      });

      // Removed alert here
    } catch (error) {
      // Error approving order value - handled silently
      showErrorToast(t('errors.failedToApproveOrderValue'), {
        variant: 'error',
        showRetry: true
      });
    }
  };

  const handleRejectOrderValue = async (orderId) => {
    try {
      const orderRef = doc(db, `organizations/${organizationId}/orders/${orderId}`);
      await updateDoc(orderRef, {
        orderValueStatus: 'rejected',
        updatedAt: serverTimestamp()
      });

      // Update local state
      setOrders(prevOrders => {
        if (Array.isArray(prevOrders)) {
          return prevOrders.map(order => 
            order.id === orderId 
              ? { ...order, orderValueStatus: 'rejected' }
              : order
          );
        }
        return prevOrders;
      });

      showErrorToast(t('errors.orderValueRejected'), {
        variant: 'info',
        showRetry: false,
        duration: 3000
      });
    } catch (error) {
      // Error rejecting order value - handled silently
      showErrorToast(t('errors.failedToRejectOrderValue'), {
        variant: 'error',
        showRetry: true
      });
    }
  };

  const closeApprovalModal = () => {
    setShowApprovalModal(false);
    setSelectedOrderForApproval(null);
  };

  // Override billPageModalProps to use the new confirmBillPageSelection
  const billPageModalProps = {
    ...originalBillPageModalProps,
    onConfirm: confirmBillPageSelection,
    onClose: () => {
      closeBillPageModal();
      setIsBillReplaceMode(false);
      setPendingBillReplaceIndex(null);
    }
  };

  // Return the hook values with simplified logic
  const returnValue = {
    orders: displayedOrders,
    loading,
    error,
    searchTerm,
    setSearchTerm: (term) => {
      // This function is now a placeholder as the searchTerm is passed as a parameter
    },
    userRole,

    loadingUserDoc: loading,
    creatorNames,
    handleApprove,
    handleEdit,
    handleEditSubmit,
    handleEditChange,
    handleDispatch,
    handleReadyToDispatch,
    handleCancel,
    handleUploadBill,
    handleReplaceBill,
    handleUploadChallan,
    handleReplaceChallan,
    handleUploadBilty,
    handleReplaceBilty,
    handleUploadProduct,
    handleReplaceProduct,
    getAvailableTabs,
    editingOrder,
    editFormData,
    setEditingOrder,
    isDispatchedView: currentTab === 'DISPATCHED',
    viewChallan,
    viewBill,
    viewBilty,
    availableTabs: getAvailableTabs(),
    deleteOrderDetailsImage,
    replaceOrderDetailsImage,
    billPageModalProps,
    handleUploadFile,
    openApprovalModal,
    handleApproveOrderValue,
    handleRejectOrderValue,
    closeApprovalModal,
    // Approval Modal Props
    showApprovalModal,
    selectedOrderForApproval,
    approvalModalProps: {
      isOpen: showApprovalModal,
      onClose: closeApprovalModal,
      currentAmount: selectedOrderForApproval?.orderValue || '',
      onApprove: handleApproveOrderValue,
      onReject: handleRejectOrderValue,
      orderId: selectedOrderForApproval?.id || ''
    },
    securityLoading,
    securityError
  };

  return returnValue;
} 